import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { UserProfile } from '@/lib/auth/types';
import { onAuthStateChange } from '@/lib/auth/core/auth';
import {
  createSubscriptionListener,
  subscribeToProfile,
} from '@/lib/auth/core/profile';
import { toUserSubscription } from '@/lib/db/services/subscriptionService';

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    loading: true,
  });

  useEffect(() => {
    let profileUnsubscribe: (() => void) | undefined;
    let subscriptionsUnsubscribe: (() => void) | undefined;

    const unsubscribe = onAuthStateChange((user) => {
      if (!user) {
        setState({ user: null, profile: null, loading: false });
        return;
      }

      // Clean up previous profile subscription if exists
      if (profileUnsubscribe) {
        profileUnsubscribe();
      }

      if (subscriptionsUnsubscribe) {
        subscriptionsUnsubscribe();
      }

      // Subscribe to profile updates
      profileUnsubscribe = subscribeToProfile(user.uid, (profile) => {
        setState({
          user,
          profile,
          loading: false,
        });
      });

      subscriptionsUnsubscribe = createSubscriptionListener(user.uid, {
        onError: () => {},
        onSubscriptionUpdate: (s) => {
          setState(({ profile, ...rest }) => {
            profile && s && (profile.subscription = toUserSubscription(s));
            return {
              profile,
              ...rest,
            };
          });
        },
      });
    });

    return () => {
      unsubscribe();
      if (profileUnsubscribe) {
        profileUnsubscribe();
      }
      subscriptionsUnsubscribe && subscriptionsUnsubscribe();
    };
  }, []);

  return state;
}
